import React, {useState, useEffect} from 'react'
import {AddressElement, useElements} from '@stripe/react-stripe-js'
import {handleError} from "../common/utils";
import agent from "../api/agent";
import { Select, Skeleton} from "antd";
import ShippingMethodForm from "./ShippingMethodForm";
import {
  addressCharLimits,
  countryText,
  displayPostalCode, filterNotes,
  regionText
} from "../common/handleAddress";

const AddressAndRatesForm = ({paymentId, setInvoiceUpdate, addresses, getAddress, warehouse, packageId, setShippingAddress, setAllowCheckout, googleAddressKey, setHasCheckoutThrownError, setRightSideLoading, usageBilling}) => {
  const [shippingRates, setShippingRates] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState({});
  const [shippingLoading, setShippingLoading] = useState(false)
  const [addressLoading, setAddressLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [addressKey, setAddressKey] = useState(Date.now())
  const [editType, setEditType] = useState('')
  const [showEditAddress, setShowEditAddress] = useState(false)
  const [returnAddress, setReturnAddress] = useState({})
  const elements = useElements();
  const [reset, setReset] = useState('')
  const [addressNote, setAddressNote] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const getShippingEstimate = async ({ addressId, isDdpEnabled }) => {
    try {
      setShippingLoading(true)
      setErrorMessage('')
      const requestBody = {
        packageId: packageId,
        addressId: addressId,
        ddp: isDdpEnabled
      }
      const response = await agent.shippingCalculator.shipEstimate(requestBody)
      const rates = response.data.data.sort((a, b) => a.amount - b.amount)

      setShippingRates(rates)
      setErrorMessage(response.data.message)
    } catch (e) {
      setAllowCheckout(false)
      setShippingRates([])
      setMessage(e.message)
      handleError(e)
    } finally {
      setShippingLoading(false)
    }
  }

  useEffect(() => {
    if (selectedAddress && selectedAddress.addressId) {
      getShippingEstimate(selectedAddress)
    }
  }, [selectedAddress])

  const handleAddressChange = (value) => {
    const address = addresses.find(addr => addr.addressId === value);

    setAddressNote(address.deliveryNotes)
    setSelectedAddress(address)
    setShippingAddress(address)
    setAllowCheckout(false)
    setShowEditAddress(false)
    setShippingLoading(true)
  };

  const handleRateChange = async ({quoteId, insurance, dataCollection}) => {
    try {
      setAllowCheckout(false)
      const checkoutInvoiceResp = await agent.checkout.checkoutReview({
        isDdp: selectedAddress.isDdpEnabled, 
        shippingQuoteId: quoteId,
        insurance,
        packageId,
        dataCollection
      })
      
      const checkoutInvoice = checkoutInvoiceResp.data.data
      const {invoiceId} = checkoutInvoice
      setInvoiceUpdate(checkoutInvoice)

      if (!usageBilling) {
        await agent.checkout.updateStripeCheckout({
          paymentIntentId: paymentId,
          invoiceId: invoiceId,
        })
      }
      
      if (addressNote) {
        const addressUpdateBody = {
          ...selectedAddress,
          deliveryNotes: addressNote,
          packageId: packageId
        }
        
        await agent.address.updateAddress(addressUpdateBody)
      }
      
      setAllowCheckout(true)

    } catch (e) {
      setMessage(e.message)
      handleError(e)
      setAllowCheckout(false)
      setHasCheckoutThrownError(true)
    } 
  }

  const handleEditAddress = async () => {
    try {
      setMessage('')
      const addressElement = elements.getElement('address');
      const result = await addressElement.getValue()
      if (!result.complete) {
        setMessage('Address form is not complete.')
        return
      }
      const addressValue = result.value
      const addressBody = {
        name: addressValue.name,
        phone: addressValue.phone,
        street1: addressValue.address.line1,
        street2: addressValue.address.line2 ? addressValue.address.line2 : '',
        city: addressValue.address.city,
        stateCode: addressValue.address?.state.replace(/\./g, '').toUpperCase(),
        countryCode: addressValue.address.country,
        postalCode: addressValue.address.postal_code,
      }

      setAddressLoading(true)
      if (editType === 'add') {
        const requestBody = {
          ...addressBody,
          packageId: packageId
        }
        await agent.address.addAddress(requestBody)
        await getAddress(warehouse)
        const newAddress = addresses[addresses.length - 1]
        setSelectedAddress(newAddress)
        setShippingAddress(newAddress)
        setEditType('')
      }

      if (editType === 'edit') {
        const requestBody = {
          ...addressBody,
          packageId: packageId,
          addressId: selectedAddress.addressId
        }
        await agent.address.updateAddress(requestBody)
        await getAddress(warehouse)
        const updatedAddr = addresses.find(addr => addr.addressId === selectedAddress.addressId)
        setSelectedAddress(updatedAddr)
        setShippingAddress(updatedAddr)
        setEditType('')
      }

      if (editType === 'return') {
        const requestBody = {
          ...addressBody,
          packageId: packageId,
        }
        setReturnAddress(addressBody)
        const response = await agent.pkg.returnPackage(requestBody)

        const { quoteId } = response.data.data
        const respHandle = await handleRateChange({quoteId, insurance: false, isDdp: selectedAddress.isDdpEnabled })

      }
      setAddressLoading(false)
      setShowEditAddress(false)
    } catch (e) {
      setAddressLoading(false)
      handleError(e)
    }
  }

  const resetAddressType = async () => {
    setAddressLoading(true)
    setShowEditAddress(false)
    setReturnAddress({})
    setSelectedAddress(addresses[0])
    setEditType('')
    setReset('')
    setAddressLoading(false)
    await getShippingEstimate(addresses[0])
  }

  const addressOptions = addresses.map(address => {return {
      value: address.addressId,
      label: `${address.name}, ${address.street1}, ${address.city}, ${address.stateCode ? address.stateCode+',' : ''} ${address.countryCode}`
    }})

  const addressEditButtonText = {
    'add': 'Add New',
    'edit': 'Edit',
    'return': 'Return'
  }

  const addressFormValue = (address) => {
    return {
      name: address.name,
      phone: address.phone ? address.phone : '',
      address: {
        line1: address.street1,
        line2: address.street2,
        city: address.city,
        state: address.stateCode,
        postal_code: address.postalCode,
        country: address.countryCode,
      }
    }
  }

  useEffect(() => {
    if (reset === 'reset') {
      resetAddressType()
    }
  }, [reset])

  const renderAddressCard = (address) => {
    return (
      <div className={"package-address-checkout"} key={address.addressId} style={{display: "flex"}} >
        <div className="package-address-content">
          <div className="ship-address-line ship-address-line-name">{address.name.substr(0, addressCharLimits.name)}</div>
          <div className="ship-address-line">{address.street1.substr(0, addressCharLimits.streets)}</div>
          {!!address.street2 &&
            <div className="ship-address-line">{address.street2.substr(0, addressCharLimits.streets)}</div>}
          {(!!address.stateCode || displayPostalCode(address.postalCode)) &&
            <div className="ship-address-line">
              {address.city.substr(0, addressCharLimits.city)}{' '}
              {address.stateCode ? regionText(address.countryCode, address.stateCode) : ''}
              {(!!address.stateCode && displayPostalCode(address.postalCode)) ? ' ' : ''}
              {displayPostalCode(address.postalCode) ? address.postalCode.substr(0, addressCharLimits.postalCode) : ''}
            </div>
          }
          <div className="ship-address-line">{countryText(address.countryCode)}</div>
        </div>
        {selectedAddress.addressId >= 0 && <div className="edit-icon">
          <i>
            <svg className="feather">
              <use href="/assets/images/icons/feather-sprite.svg#edit"/>
            </svg>
          </i>
        </div>}
      </div>
    )
  }

  const deliveryNote = (
    <div className="address-form-group pt-2">
      <div className="address-form-label">Delivery Notes</div>
      <textarea className="address-form-notes"
                onChange={(e) => setAddressNote(filterNotes(e.target.value))}
                value={addressNote} maxLength={addressCharLimits.notes}/>
      <div className="sub">Delivery notes will be printed and added to package for courier/recipient where possible.
        Optional. Max 500 characters.
      </div>
    </div>
  )


  useEffect(() => {
    if (editType === 'add' || editType === 'return') {
      setSelectedAddress({
        name: '',
        phone: '',
        street1: '',
        street2: '',
        city: '',
        stateCode: '',
        postalCode: '',
        countryCode: '',
      })
    }

  }, [editType])

  const filterOption = (input, option) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  useEffect(() => {
    if (!!selectedAddress) {
      setAddressKey(Date.now());
      setAddressNote(selectedAddress.notes)
    }
  }, [selectedAddress]);

  return (
      <div>
        {editType !== 'return' ? <h4>Select Shipping Address</h4> : <h4>Return Address</h4>}
        {editType !== 'return' && <div className="address-dropdown">
          <div className="address-dropdown-select">
            <Select
              placeholder="Please Select Address"
              onChange={handleAddressChange}
              style={{width: '100%', marginBottom: '20px'}}
              value={selectedAddress?.addressId}
              options={addressOptions}
              disabled={shippingLoading}
              showSearch
              filterOption={filterOption}
              optionFilterProp="label"
            />
          </div>
          <div className="btn btn-primary address-dropdown-button" onClick={() => {if(!shippingLoading) {setEditType('add'); setShowEditAddress(true)}}}><span>+ Address</span></div>
        </div>
        }

        {editType === 'return' && <div className="notice notice-info">
          <div className="notice-content">If you have a vendor-provided returns label for returning a package to a vendor:
            <br/>1. Enter the label address below
            <br/>2. Complete the checkout process
            <br/>3. Email our customer service team the returns shipping label
            <br/>
            <br/>The address or vendor on the shipping label must match the original sender.
          </div>
        </div>}

        {!showEditAddress && selectedAddress?.name && <div onClick={() => {if (selectedAddress.addressId >= 0 && !shippingLoading) {
          setEditType('edit');
          setShowEditAddress(true);
        }}}>
          {renderAddressCard(selectedAddress)}
        </div>}

        {!!!editType && selectedAddress?.name && selectedAddress.addressId > 0 && deliveryNote}

        {/*{!!!editType && <div className="checkout-package-return"><span onClick={() => {setEditType('return'); setShowEditAddress(true); setAllowCheckout(false)}}>I have a vendor return shipping label</span></div>}*/}

        {editType === 'return' && !showEditAddress && returnAddress.name && <div onClick={() => {setEditType('return'); setShowEditAddress(true)}}>
          {renderAddressCard(returnAddress)}

          <div className="checkout-package-return"><span onClick={() => {setReset('reset'); setAllowCheckout(false)}}>Use Other Addresses</span></div>
        </div>}

        {addressLoading ? <div className="pt-2"><Skeleton active={true}/></div> :
        showEditAddress && <div>
            <h5>{addressEditButtonText[editType]} Address</h5>
            <AddressElement
              key={addressKey}
              disabled={true}
              options={{
                mode: 'shipping',
                fields: {
                  phone: 'always'
                },
                validation: {
                  phone: {
                    required: 'always'
                  }
                },
                autocomplete: {
                  mode: 'google_maps_api',
                  apiKey: googleAddressKey
                },
                blockPoBox: true,
                defaultValues: editType !== 'return' ? addressFormValue(selectedAddress) : addressFormValue(returnAddress)
              }}
            />
            <div style={{display: "flex", justifyContent: 'flex-end'}}>
              <div className="btn btn-primary mt-2 ml-2" onClick={() => handleEditAddress()} >Save Address</div>
              <div className="btn btn-primary mt-2 ml-2" onClick={() => resetAddressType()} >Cancel</div>
            </div>
            {message && <div className="error-text">{message}</div>}
          </div>
        }

        <div className="mt-3 mb-3">
          {(shippingRates.length > 0 || shippingLoading) && !!!editType && selectedAddress.addressId && <h4>Select Shipping Rates</h4>}
        </div>
        {shippingLoading ? <Skeleton active={true}/> :
          <div>
            {!showEditAddress && shippingRates.length > 0 && editType !== 'return' &&
              <ShippingMethodForm quotes={shippingRates} handleRateChange={handleRateChange} setAllowCheckout={setAllowCheckout} setRightSideLoading={setRightSideLoading}/>}
          </div>
        }
        {!!errorMessage && <div className="ship-quotes-none">Sorry, we cannot provide shipping quotes from other carriers, due to: <br/>{errorMessage}</div>}
      </div>
  )


}

export default AddressAndRatesForm